import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import { API_ROUTES } from "../../../../api/ApiRoutes";
import createPrivateAdminPanelInstance from "../../../../api/PrivateAdminPanelInstance";

dayjs.extend(utc);

const METRICS = [
  "total_rebet_cash",
  "redeemable_rebet_cash",
  "total_coins",
  "pikkem_cash",
  "pikkem_redeemable",
];

const DAY_IN_MS = 24 * 60 * 60 * 1000;
const DEFAULT_POINTS = 30;

const buildMetricSeries = ({ points, startValue, minValue, maxStep, decimals = 2 }) => {
  const now = Date.now();
  const startTimestamp = now - (points - 1) * DAY_IN_MS;
  let currentValue = startValue;

  return Array.from({ length: points }, (_, index) => {
    const timestamp = startTimestamp + index * DAY_IN_MS;
    const randomStep = (Math.random() * 2 - 1) * maxStep;
    currentValue = Math.max(minValue, currentValue + randomStep);
    const roundedValue = Number(currentValue.toFixed(decimals));
    return [timestamp, roundedValue];
  });
};

const buildMockUserWalletGraphResponse = (userId) => {
  const series = {
    total_rebet_cash: buildMetricSeries({
      points: DEFAULT_POINTS,
      startValue: 23500,
      minValue: 1000,
      maxStep: 500,
      decimals: 2,
    }),
    redeemable_rebet_cash: buildMetricSeries({
      points: DEFAULT_POINTS,
      startValue: 20500,
      minValue: 500,
      maxStep: 450,
      decimals: 2,
    }),
    total_coins: buildMetricSeries({
      points: DEFAULT_POINTS,
      startValue: 50000,
      minValue: 1000,
      maxStep: 1500,
      decimals: 0,
    }),
    pikkem_cash: buildMetricSeries({
      points: DEFAULT_POINTS,
      startValue: 320,
      minValue: 0,
      maxStep: 35,
      decimals: 2,
    }),
    pikkem_redeemable: buildMetricSeries({
      points: DEFAULT_POINTS,
      startValue: 180,
      minValue: 0,
      maxStep: 25,
      decimals: 2,
    }),
  };

  const summary = Object.fromEntries(
    Object.entries(series).map(([metricKey, metricSeries]) => [
      metricKey,
      { current: metricSeries[metricSeries.length - 1]?.[1] || 0 },
    ])
  );

  return {
    user_id: userId || "user-123",
    timezone: "UTC",
    metrics: METRICS,
    series,
    summary,
  };
};

/**
 * Calendar YYYY-MM-DD for API query params.
 * - dayjs (e.g. from CustomDatePicker / timezone): use its calendar day (endOf("today") must not become tomorrow via UTC).
 * - native Date: UTC calendar date (matches date-only strings and existing callers/tests).
 */
const toYYYYMMDD = (d) => {
  if (d == null) return null;
  if (typeof d?.format === "function") {
    return d.format("YYYY-MM-DD");
  }
  return dayjs.utc(d).format("YYYY-MM-DD");
};

const DEFAULT_RANGE_DAYS = 30;

/**
 * Fetches balance history for the wallet graph from GET /admin-panel/feature-management/v1/{user_id}/balance-history.
 * Request: user_id (path), start_date and end_date (query, YYYY-MM-DD).
 * Expects response shape: { user_id?, timezone?, metrics?, series?, summary? } (or wrapped in response.data).
 */
export const getUserWalletGraphData = async ({ userId, date, startDate, endDate }) => {
  if (!userId) return buildMockUserWalletGraphResponse(userId);

  const token = JSON.parse(localStorage.getItem("token") || "null");
  const client = createPrivateAdminPanelInstance(token?.AccessToken);
  const url = API_ROUTES.GET_USER_BALANCE_HISTORY.replace("{user_id}", String(userId));

  const params = {};
  if (startDate != null && endDate != null) {
    params.start_date = toYYYYMMDD(startDate);
    params.end_date = toYYYYMMDD(endDate);
  } else if (date) {
    const end = Array.isArray(date) ? date[date.length - 1] : date;
    const endStr = toYYYYMMDD(end);
    if (endStr) {
      const endD = new Date(endStr);
      const startD = new Date(endD);
      startD.setDate(startD.getDate() - (DEFAULT_RANGE_DAYS - 1));
      params.start_date = toYYYYMMDD(startD);
      params.end_date = endStr;
    }
  }
  const res = await client.get(url, { params });
  const payload = res?.data?.data ?? res?.data ?? res;

  if (!payload || typeof payload !== "object" || !Array.isArray(payload.tabs)) {
    return buildMockUserWalletGraphResponse(userId);
  }

  // API always returns { tabs, series, overview }
  const metrics = payload.tabs;
  const summary = Object.fromEntries(
    Object.entries(payload.overview || {}).map(([key, v]) => [
      key,
      { current: v?.current_balance ?? 0, pct_change: v?.pct_change },
    ])
  );
  return {
    user_id: payload.user_id ?? userId,
    start_date: payload.start_date,
    end_date: payload.end_date,
    timezone: payload.timezone ?? "UTC",
    metrics,
    series: payload.series ?? {},
    summary,
  };
};

const userWalletGraphDataSource = {
  getUserWalletGraphData,
};

export default userWalletGraphDataSource;
